'use client';
import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';
import { useSetAtom } from 'jotai';
import clsx from 'clsx';

import { AccountActStatus, KafkaNote, PromotionType } from '@nf/utils-common/constants';
import { LicenseeTypeEnums } from '@nf/types/site';
import webStorageKeys from '@/constants/web-storage-keys';
import { APPBAR_ITEM_CONFIG, type AppbarItemKey, type AppbarItemConfig } from '../../appbar-settings';
import { isQuickSettingsOpenAtom } from '@/atoms/quick-settings';
import { useViewMode } from '@/store/appearance';
import { siteService } from '@/services/site.service';
import { profilePanelService } from '@/services/profile-panel.service';
import { modalService } from '@/services/modal.service';
import { useObservableState } from '@nf/utils-common/hooks';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { useSportMenuItemGetter } from '@/hooks/query';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';

import { AppbarItem } from '../appbar-item';
import { AppbarItemWithTicketCount } from '../appbar-item-with-ticket-count';
import { HighlightEffect } from '../highlight-effect';
import { QuickSettingsLoader } from '@/features/quick-settings/components/quick-settings-loader';

import { appbarStyles } from '../../appbar.style';

export const AppbarContent = ({ pathSegments }: { pathSegments: string[] }) => {
	const router = useRouter();
	const locale = useLocale();

	const setIsShowQuickSettings = useSetAtom(isQuickSettingsOpenAtom);

	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	
	const { disableParlay } = useOddsPageSwitcher();
	const { getSportMenuItemsOfMode } = useSportMenuItemGetter();
	const { isMars } = useViewMode();

	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;

	const isPromotionPage = pathSegments.includes('/promotion');
	const isOddsPage = pathSegments.includes('/(main)') && !isPromotionPage;
	const isMePageOpened = useObservableState(profilePanelService.onProfilePanel$, false);

	const enableHomePage = SiteConfigsData?.EnableLicLobby;
	const enablePromotionPage = (SiteConfigsData?.EnablePromotion ?? 0 >  PromotionType.Close);

	const lastItemKey: AppbarItemKey = !isOddsPage || isMePageOpened ? 'Me' : 'Settings';
	
	const depositAppbarItemList: Array<AppbarItemKey> = SiteConfigsData?.EnableMyFavorite
		? ['Home', 'Favorite', 'Sports', 'Statement', lastItemKey]
		: ['Home', 'Sports', 'Statement', lastItemKey];

	let appbarItemList: Array<AppbarItemKey> = user?.ActStatus === AccountActStatus.Deposited
		? depositAppbarItemList
		: ['Home', 'Sports', 'Me'];

	if (!enableHomePage || (enableHomePage && enablePromotionPage && (!isPromotionPage || isMePageOpened))) {
		appbarItemList = appbarItemList.filter(item => item !== 'Home');

		if (enablePromotionPage) {
			appbarItemList = ['Promotion', ...appbarItemList];
		}
	}

	if (isAthena) {
		const index = appbarItemList.indexOf('Sports');
		appbarItemList = [
			...appbarItemList.slice(0, index + 1),
			'Transaction',
			...appbarItemList.slice(index + 1)
		];
	}

	const [showGoldenDuelTooltip, setShowGoldenDuelTooltip] = useState(false);
	const [needShowTooltip, setNeedShowTooltip] = useState(false);

	useEffect(() => {
		try {
			const hasShown = localStorage.getItem(webStorageKeys.HAS_SHOWN_MARS_UPDATE_TOOLTIP_IN_APPBAR);

			if (hasShown !== 'true') {
				setNeedShowTooltip(true);
			}
		} catch (error: any) {
			console.error(error);
		}
	}, []);
	
	useEffect(() => {
		const subscription = modalService.onOpenGoldenDuelToolTipModal.subscribe((isOpen) => {
			if (SiteConfigsData?.EnableGoldenDuel) {
				setShowGoldenDuelTooltip(isOpen);
			}
		});
	
		return () => subscription.unsubscribe();
	}, [SiteConfigsData?.EnableGoldenDuel]);

	const checkIsSelected = (config: AppbarItemConfig) => 
		(isMePageOpened && config.key === 'Me') || 
			(!isMePageOpened && 
				pathSegments.includes(config.routePrefix ?? '') &&
				(!config.routePrefixExclude || 
					config.routePrefixExclude.every(route => !pathSegments.includes(route))
				)
			);

	const getCustomHref = (href: string) => {
		const menuData = getSportMenuItemsOfMode('M0');
		const defaultSportHref = menuData.at(0)?.href ?? '';
		return href.startsWith('/sports') && defaultSportHref ? defaultSportHref : href;
	};

	const getClickAction = (config: AppbarItemConfig): (() => void) | undefined => {
		if (config.actionType === 'open-profile') {
			return () => profilePanelService.displayProfilePanel(true, true);
		}
		if (config.actionType === 'toggle-settings') {
			return () => setIsShowQuickSettings(prev => !prev);
		}
		return undefined;
	};

	const setKafka = (config: AppbarItemConfig) => {
		if (!config.kafkaAction) return;
	
		setKafkaNormal({
			note: KafkaNote.MarsV1,
			type: 4,
			action: config.kafkaAction
		});
	};
	
	/** 針對 home page 下的點擊事件埋點 */
	const setSpecialKafka = (config: AppbarItemConfig) => {
		if (!config.specialKafkaAction) return;
	
		const isHomePage = pathSegments.includes('/lobby');
		if (isHomePage && !isMePageOpened) {
			setKafkaNormal({
				note: KafkaNote.MarsV1,
				type: 1,
				action: config.specialKafkaAction
			});
		}
	};

	const clickItem = (config: AppbarItemConfig) => {
		if (checkIsSelected(config)) return;

		setKafka(config);
		setSpecialKafka(config);
		setNeedShowTooltip(localStorage.getItem(webStorageKeys.HAS_SHOWN_MARS_UPDATE_TOOLTIP_IN_APPBAR) !== 'true');

		if (config.key !== 'Me' && isMePageOpened) {
			profilePanelService.displayProfilePanel(false);
		}

		if (config.key !== 'Settings') {
			disableParlay();
		}

		if (config.href) {
			const newHref = getCustomHref(config.href);
			router.push(newHref, { locale });
		} else {
			getClickAction(config)?.();
		}
	};

	return (
		<>
			<div className={clsx('appbar', appbarStyles.root, { [appbarStyles.appbarHoist]: isMePageOpened })}>
				{appbarItemList.map(itemKey => {
					const config = APPBAR_ITEM_CONFIG[itemKey];
					const AppbarItemComponent = config.key === 'Statement' 
						? AppbarItemWithTicketCount 
						: AppbarItem;
		
					return (
						<AppbarItemComponent 
							key={config.key}
							config={config}
							isSelected={checkIsSelected(config)}
							isMars={isMars}
							isValidPromotionVoucherUser={Boolean(user?.IsValidPromotionVoucherUser)}
							enableMarsRevisionNotice={Boolean(SiteConfigsData?.EnableMarsRevisionNotice)}
							showGoldenDuelTooltip={showGoldenDuelTooltip}
							needShowTooltip={needShowTooltip}
							onTooltipShown={() => setNeedShowTooltip(false)}
							onClick={() => clickItem(config)}
						/>
					);
				})}
				<HighlightEffect />
			</div>
			<QuickSettingsLoader />
		</>
	);
};